import React, { useState, useMemo } from 'react';
import { HeartIcon, PlusIcon, HandsClappingIcon, UserGroupIcon } from '@revfluence/fresh-icons/solid/esm';
import { InstagramIcon, PinterestIcon, YoutubeIcon, TiktokIcon } from '@revfluence/fresh-icons/brands/esm';
import { useTopCreatorsData } from '@frontend/applications/Social/Analytics/hooks';
import { useAuth } from '@frontend/context/authContext';
import { calculateDatesFromType } from '@frontend/applications/Social/utils/dates';
import { GetSocialAnalyticsMembersQuery } from '@frontend/gql/social/graphql';
import { useCommunitiesQuery, useProspectsAddToCommunity, useProspectsRemoveFromCommunity } from '@frontend/app/hooks';
import { GetCommunitiesQuery_communities as ICommunity } from '@frontend/app/queries/types/GetCommunitiesQuery';
import { ISocialAccount } from '@components';
import { getErrorMessageFromGraphQL } from '@frontend/utils';
import { useGroupsForSocialAccounts } from '@frontend/components/widgets/Invite/hooks/useGroupsForSocialAccounts';
import { backendServerApiEndpoint } from '@frontend/applications/Shared/serviceHosts';
import { Button } from '@/shadcn/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/shadcn/components/ui/avatar';
import { Card, CardContent } from '@/shadcn/components/ui/card';
import { Skeleton } from '@/shadcn/components/ui/skeleton';
import { useToast } from '@/shadcn/hooks/use-toast';
import Empty from '@/app/refresh-components/Empty';
import ProspectsOverview from '../../../ProjectsPage/ProspectsList/ProspectsOverview';

interface CreatorData {
  id: string;
  username: string;
  name: string;
  platform: 'instagram' | 'tiktok' | 'youtube' | 'pinterest';
  followers: string;
  engagement: string;
  profileImage: string;
  metrics: {
    reach: number | null;
    impressions: number;
    likes: number | null;
    engagementRate: number | null;
    tmv: number;
  };
}

export const MY_FAVORITES_TITLE = 'My Favorites';

// Helper function to convert creator data to prospect format
const getProspectFromCreator = (creator: CreatorData) => ({
  network_identifier: creator.platform,
  instagram_username: creator.platform === 'instagram' ? creator.username : undefined,
  tiktok_username: creator.platform === 'tiktok' ? creator.username : undefined,
  youtube_username: creator.platform === 'youtube' ? creator.username : undefined,
  pinterest_username: creator.platform === 'pinterest' ? creator.username : undefined,
  name: creator.name,
  profile_picture_url: creator.profileImage,
});

// Helper function to convert creator data to social account format
const getSocialAccountFromCreator = (creator: CreatorData): ISocialAccount => ({
  id: Number(creator.id) || 0,
  network_identifier: creator.platform,
  full_display_name: creator.name,
  username: creator.username,
  profile_picture: creator.profileImage,
  has_email: false,
  email: undefined,
});

const formatNumber = (num: number | null): string => {
  if (num === null) return 'N/A';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

interface MemberData {
  id: string;
  name?: string;
  profilePicture?: string;
  instagramUsername?: string;
  tiktokUsername?: string;
  youtubeUsername?: string;
  pinterestUsername?: string;
  segment?: string;
}

const determinePlatform = (member: MemberData): 'instagram' | 'tiktok' | 'youtube' | 'pinterest' => {
  if (member.instagramUsername) return 'instagram';
  if (member.tiktokUsername) return 'tiktok';
  if (member.youtubeUsername) return 'youtube';
  if (member.pinterestUsername) return 'pinterest';
  return 'instagram'; // default
};

const transformApiDataToCreators = (apiData: GetSocialAnalyticsMembersQuery): CreatorData[] => {
  if (!apiData?.client?.socialAnalytics?.members?.results) return [];

  return apiData.client.socialAnalytics.members.results.map((result) => {
    const member = result.member;
    const total = result.total;
    if (!member) {
      return {
        id: result.memberId || 'unknown',
        username: 'unknown',
        name: 'Unknown Creator',
        platform: 'instagram' as const,
        followers: '0',
        engagement: '0',
        profileImage: 'https://placehold.co/40x40',
        metrics: {
          reach: null,
          impressions: 0,
          likes: null,
          engagementRate: null,
          tmv: 0,
        }
      };
    }

    const platform = determinePlatform(member);

    const username = platform === 'instagram' ? member.instagramUsername :
                    platform === 'tiktok' ? member.tiktokUsername :
                    platform === 'youtube' ? member.youtubeUsername :
                    platform === 'pinterest' ? member.pinterestUsername :
                    member.instagramUsername || 'unknown';

    return {
      id: member.id || result.memberId,
      username: username || 'unknown',
      name: member.name || 'Unknown Creator',
      platform,
      followers: formatNumber(total?.potentialReach || 0),
      engagement: formatNumber(total?.engagements || 0),
      profileImage: member.profilePicture || 'https://placehold.co/40x40',
      metrics: {
        reach: total?.reach || null,
        impressions: total?.impressions || 0,
        likes: total?.likes || null,
        engagementRate: total?.engagementRate || null,
        tmv: total?.tmv || 0,
      }
    };
  });
};

interface CreatorCardProps {
  creator: CreatorData;
  communities: ICommunity[];
  onFavoriteToggle?: (creator: CreatorData, isFavorited: boolean) => void;
}

const CreatorCard: React.FC<CreatorCardProps> = ({ creator, communities, onFavoriteToggle }) => {
  const { toast } = useToast();
  const [addProspectsToGroup] = useProspectsAddToCommunity();
  const [removeProspectsFromGroup] = useProspectsRemoveFromCommunity();
  const [socialDrawerOpen, setSocialDrawerOpen] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const { clientInfo } = useAuth();
  const myFavoritesGroup = useMemo(() => {
    return communities.find((group) => group.title === MY_FAVORITES_TITLE);
  }, [communities]);

  // Convert creator to social account for the hook
  const socialAccount = useMemo(() => getSocialAccountFromCreator(creator), [creator]);

  // Use the hook to fetch groups this creator is already in
  const {
    groups: invitedGroups,
    isFetching: isFetchingGroups,
    refetch: refetchGroupMembership,
  } = useGroupsForSocialAccounts({
    socialAccounts: [socialAccount],
    apiEndpointParam: backendServerApiEndpoint(),
    clientIdParam: clientInfo.id,
  });

  // Check if creator is already in favorites
  const isFavorited = useMemo(() => {
    if (!myFavoritesGroup) return false;
    return !!invitedGroups.find((group) => group.id === myFavoritesGroup.id);
  }, [invitedGroups, myFavoritesGroup]);

  const getPlatformIcon = () => {
    switch (creator.platform) {
      case 'instagram':
        return InstagramIcon;
      case 'tiktok':
        return TiktokIcon;
      case 'youtube':
        return YoutubeIcon;
      case 'pinterest':
        return PinterestIcon;
      default:
        return InstagramIcon;
    }
  };

  const PlatformIcon = getPlatformIcon();

  const handleFavoriteToggle = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!myFavoritesGroup || isTogglingFavorite) return;

    try {
      setIsTogglingFavorite(true);
      const prospect = getProspectFromCreator(creator);

      if (isFavorited) {
        await removeProspectsFromGroup({
          variables: {
            prospects: [prospect],
            communityIds: [myFavoritesGroup.id],
          },
        });
        toast({
          title: 'Success',
          description: 'Removed from My Favorites',
          variant: 'success',
        });
      } else {
        await addProspectsToGroup({
          variables: {
            prospects: [prospect],
            communityIds: [myFavoritesGroup.id],
            source: 'creator_recommendation',
          },
        });
        toast({
          title: 'Success',
          description: 'Added to My Favorites',
          variant: 'success',
        });
      }

      // Refetch group membership to update the UI
      if (refetchGroupMembership) {
        await refetchGroupMembership();
      }

      onFavoriteToggle?.(creator, !isFavorited);
    } catch (error) {
      toast({
        title: 'Error',
        description: getErrorMessageFromGraphQL(error),
        variant: 'destructive',
      });
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  return (
    <>
      <Card className="rounded-2xl border border-border overflow-hidden w-[278px] min-w-[278px]" onClick={() => setSocialDrawerOpen(true)}>
        <div className="flex-1 flex h-96 items-center relative">
          <img src={creator.profileImage} alt={creator.name} className="h-full w-full object-cover absolute inset-0 blur-sm" />
          <Avatar className="h-auto w-full rounded-none">
            <AvatarImage src={creator.profileImage} alt={creator.name} />
            <AvatarFallback className="text-2xl font-medium">{creator.name.charAt(0)}</AvatarFallback>
          </Avatar>
        </div>
        <CardContent className="p-3 bg-grey-1 flex flex-col gap-2">
          <div className="flex items-start gap-2">
            <div className="relative">
              <Avatar className="w-10 h-10">
                <AvatarImage src={creator.profileImage} alt={creator.name} />
                <AvatarFallback>{creator.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-background rounded-full flex items-center justify-center border border-border">
                <PlatformIcon className="w-3 h-3 text-foreground" />
              </div>
            </div>
            <div className="flex-1 flex flex-col justify-center">
              <div className="text-foreground text-xs font-medium font-inter leading-tight">{creator.username}</div>
              <div className="text-foreground text-xs font-normal font-inter leading-tight">{creator.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 flex justify-between items-center">
              <div className="flex items-center gap-1">
                <UserGroupIcon className="w-3 h-3 text-grey-5" />
                <span className="text-foreground text-xs font-medium font-inter leading-tight">{creator.followers}</span>
              </div>
              <div className="flex items-center gap-1">
                <HandsClappingIcon className="w-3 h-3 text-grey-5" />
                <span className="text-foreground text-xs font-medium font-inter leading-tight">{creator.engagement}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={handleFavoriteToggle}
                disabled={isTogglingFavorite || !myFavoritesGroup || isFetchingGroups}
              >
                <HeartIcon className={`h-3 w-3 ${isFavorited ? 'text-red-500 fill-current' : ''}`} />
              </Button>
              <Button size="sm" className="h-6 p-1.5">
                <PlusIcon className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <ProspectsOverview
        open={socialDrawerOpen}
        onOpenChange={setSocialDrawerOpen}
        member={null}
        isLoading={false}
        socialData={null}
        isFetchingSocialData={false}
        project={null}
        refetchData={() => {}}
        usernameProp={creator.username}
        platformProp={creator.platform}
        allMembers={[]}
      />
    </>
  );
};

// Skeleton component for loading state
const CreatorCardSkeleton: React.FC = () => {
  return (
    <Card className="rounded-2xl border border-border overflow-hidden w-[278px] min-w-[278px]">
      <div className="flex-1 flex h-96 items-center relative">
        <Skeleton className="h-full w-full absolute inset-0" />
      </div>
      <CardContent className="p-3 bg-grey-1 flex flex-col gap-2">
        <div className="flex items-start gap-2">
          <div className="relative">
            <Skeleton className="w-10 h-10 rounded-full" />
            <Skeleton className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full" />
          </div>
          <div className="flex-1 flex flex-col justify-center gap-1">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 flex justify-between items-center">
            <div className="flex items-center gap-1">
              <Skeleton className="w-3 h-3 rounded" />
              <Skeleton className="h-3 w-12" />
            </div>
            <div className="flex items-center gap-1">
              <Skeleton className="w-3 h-3 rounded" />
              <Skeleton className="h-3 w-10" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-6 rounded" />
            <Skeleton className="h-6 w-6 rounded" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface CreatorsProps {
  projectIds: number[];
}

export const Creators = ({ projectIds }: CreatorsProps) => {
  const { clientInfo } = useAuth();

  const { data: communities } = useCommunitiesQuery();

  const { startDate, endDate } = calculateDatesFromType('allTime');

  const { data: topCreatorsData, fetching: topCreatorsFetching } = useTopCreatorsData({
    clientId: clientInfo.id,
    queryVariables: {
      clientId: clientInfo.id,
      startDate,
      endDate,
      includeEstimates: true,
      networks: [],
      postTypes: [],
      groupIds: [],
      includeAds: true,
      lifetimeInsightsEnabled: false,
      memberIds: [],
      projectIds,
      mentionSources: [],
      postDateIntervalEnabled: false,
      segments: [],
    },
    pause: !clientInfo.id,
  });

  const creators = transformApiDataToCreators(topCreatorsData);

  if (topCreatorsFetching) {
    return (
      <div className="flex gap-4 overflow-x-auto">
        {Array.from({ length: 4 }).map((_, index) => (
          <CreatorCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (!creators.length) {
    return (
      <div className="p-4">
        <Empty
          icon={UserGroupIcon}
          title="No creators found"
          description="We couldn't find any recommended creators for your projects at this time."
          layout="horizontal"
          size="lg"
        />
      </div>
    );
  }

  return (
    <div className="flex gap-4 overflow-x-auto">
      {creators.slice(0, 4).map((creator) => (
        <CreatorCard
          key={creator.id}
          creator={creator}
          communities={communities?.communities || []}
          onFavoriteToggle={(creator, isFavorited) => {
            // Optional: Handle favorite toggle callback
            console.log(`Creator ${creator.name} ${isFavorited ? 'added to' : 'removed from'} favorites`);
          }}
        />
      ))}
    </div>
  );
};
